import React, { useState, useEffect, useCallback } from 'react';
import { useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Search, Plus, Upload, Edit2, UserCheck, UserX, 
  ChevronLeft, ChevronRight, X, Check, AlertCircle, CalendarClock 
} from 'lucide-react';
import VeyonikaAvailabilityModal from '../components/VeyonikaAvailabilityModal';
import AppHeader from '../components/AppHeader';

const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BASE_ROLES = [
  { value: 'student', label: 'Care-Seeker' },
  { value: 'care_provider', label: 'Care Provider' },
  { value: 'admin', label: 'Admin' }
];

// Display labels only. Internal `value`s are unchanged (stored in DB + used in
// queries/escalation logic) to avoid migration/regression risk.
const SUB_ROLES = [
  { value: 'peer_counsellor', label: 'Peer Support Companion' },
  { value: 'womens_counsellor', label: "Women's Counsellor" },
  { value: 'counselling_psychologist', label: 'Counselling Psychologist' },
  { value: 'therapist', label: 'Therapist' },
  { value: 'psychiatrist', label: 'Psychiatrist' },
  { value: 'faculty_mentor', label: 'Wellness Mentor' },
  { value: 'supervisor', label: 'Program Supervisor' },
  { value: 'student_welfare_head', label: "Women's Support Lead" }
];

// Resolve a base-role/legacy-role value to a user-facing label.
const roleLabel = (value) => {
  if (value === 'student' || value === 'care_seeker') return 'Care-Seeker';
  return BASE_ROLES.find(r => r.value === value)?.label || value;
};

export default function AdminUsersPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [baseRoleFilter, setBaseRoleFilter] = useState('');
  const [subRoleFilter, setSubRoleFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Create form
  const [newUser, setNewUser] = useState({
    email: '', name: '', base_role: 'student', sub_role: '', 
    department: '', campus: '', phone: '', whatsapp_number: ''
  });
  
  // Upload state
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Veyonika availability modal state
  const [availabilityProvider, setAvailabilityProvider] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      if (search) params.append('search', search);
      if (baseRoleFilter) params.append('base_role', baseRoleFilter);
      if (subRoleFilter) params.append('sub_role', subRoleFilter);
      
      const res = await fetch(`${API_URL}/api/mvp/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [token, page, search, baseRoleFilter, subRoleFilter]);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.base_role !== 'admin') {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, navigate, fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/mvp/admin/users`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(newUser)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create user');
      
      alert(`User created! ${data.temp_password ? `Temporary password: ${data.temp_password}` : ''}`);
      setShowCreateModal(false);
      setNewUser({ email: '', name: '', base_role: 'student', sub_role: '', department: '', campus: '', phone: '', whatsapp_number: '' });
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/mvp/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error('Failed to update user');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    await handleUpdateUser(userId, { is_active: !currentStatus });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/mvp/admin/users/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setUploadResult(data);
      fetchUsers();
    } catch (err) {
      setUploadResult({ error: err.message });
    } finally {
      setUploading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title="User Management" />
      <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Users className="text-blue-600" size={28} />
            <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Upload size={18} />
              <span className="hidden sm:inline">Bulk Upload</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Create User</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search name, email, ID..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={baseRoleFilter}
              onChange={(e) => { setBaseRoleFilter(e.target.value); setPage(0); }}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Roles</option>
              {BASE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select
              value={subRoleFilter}
              onChange={(e) => { setSubRoleFilter(e.target.value); setPage(0); }}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sub-Roles</option>
              {SUB_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <div className="text-sm text-gray-600 flex items-center">
              Total: <span className="font-semibold ml-1">{total}</span> users
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub-Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                      <td className="px-4 py-3">
                        {editingUser === u.id ? (
                          <select
                            defaultValue={u.base_role || u.role}
                            onChange={(e) => handleUpdateUser(u.id, { base_role: e.target.value })}
                            className="text-xs border rounded px-2 py-1"
                          >
                            {BASE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                            {roleLabel(u.base_role || u.role)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingUser === u.id ? (
                          <select
                            defaultValue={u.sub_role || ''}
                            onChange={(e) => handleUpdateUser(u.id, { sub_role: e.target.value || null })}
                            className="text-xs border rounded px-2 py-1"
                          >
                            <option value="">None</option>
                            {SUB_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        ) : (
                          u.sub_role && (
                            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                              {SUB_ROLES.find(r => r.value === u.sub_role)?.label || u.sub_role}
                            </span>
                          )
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {u.department && <div>{u.department}</div>}
                        {u.campus && <div className="text-xs text-gray-400">{u.campus}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${u.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {u.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {editingUser === u.id ? (
                            <button onClick={() => setEditingUser(null)} className="text-green-600 hover:text-green-800">
                              <Check size={18} />
                            </button>
                          ) : (
                            <button onClick={() => setEditingUser(u.id)} className="text-blue-600 hover:text-blue-800">
                              <Edit2 size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleToggleActive(u.id, u.is_active !== false)}
                            className={u.is_active !== false ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}
                          >
                            {u.is_active !== false ? <UserX size={18} /> : <UserCheck size={18} />}
                          </button>
                          {(u.base_role === 'care_provider' || u.role === 'care_provider') && u.is_active !== false && (
                            <button
                              onClick={() => setAvailabilityProvider({ id: u.id, name: u.name })}
                              title="Veyonika availability"
                              className="text-purple-600 hover:text-purple-800"
                              data-testid={`veyonika-availability-btn-${u.id}`}
                            >
                              <CalendarClock size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1 px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1 px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Create New User</h2>
                <button onClick={() => setShowCreateModal(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="user@example.edu.in"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <select
                      value={newUser.base_role}
                      onChange={(e) => setNewUser({...newUser, base_role: e.target.value, sub_role: e.target.value === 'student' ? '' : newUser.sub_role})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      {BASE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  {newUser.base_role !== 'student' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Role</label>
                      <select
                        value={newUser.sub_role}
                        onChange={(e) => setNewUser({...newUser, sub_role: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">Select...</option>
                        {SUB_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                    <input
                      type="text"
                      value={newUser.department}
                      onChange={(e) => setNewUser({...newUser, department: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Campus</label>
                    <input
                      type="text"
                      value={newUser.campus}
                      onChange={(e) => setNewUser({...newUser, campus: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create User
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Bulk Upload Care-Seekers</h2>
                <button onClick={() => { setShowUploadModal(false); setUploadResult(null); }}><X size={20} /></button>
              </div>
              
              <div className="mb-4 p-4 bg-blue-50 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-2">CSV Format (max 100 rows):</p>
                <code className="text-xs text-blue-700">
                  name, email, gender, whatsapp_number, student_id, course, year, batch, hostel
                </code>
              </div>
              
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={uploading}
                className="w-full mb-4"
              />
              
              {uploading && <p className="text-blue-600">Uploading...</p>}
              
              {uploadResult && (
                <div className={`p-4 rounded-lg ${uploadResult.error ? 'bg-red-50' : 'bg-green-50'}`}>
                  {uploadResult.error ? (
                    <p className="text-red-700">{uploadResult.error}</p>
                  ) : (
                    <>
                      <p className="text-green-800 font-medium">
                        Imported {uploadResult.successful} of {uploadResult.total_rows} care-seekers
                      </p>
                      {uploadResult.failed > 0 && (
                        <div className="mt-2 text-sm text-red-700">
                          <p className="font-medium">{uploadResult.failed} failed:</p>
                          <ul className="list-disc pl-5 mt-1 max-h-32 overflow-y-auto">
                            {uploadResult.errors?.slice(0, 10).map((err, i) => (
                              <li key={i}>Row {err.row}: {err.error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <VeyonikaAvailabilityModal
        isOpen={!!availabilityProvider}
        providerId={availabilityProvider?.id}
        providerName={availabilityProvider?.name}
        token={token}
        onClose={() => setAvailabilityProvider(null)}
      />
      </div>
    </div>
  );
}
